import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * A conversation picture shown larger, in the SAME window: a dimmed layer over the console with the picture centred,
 * closed by Esc, a click outside the picture, or the close button. It replaces opening the picture in a new tab.
 *
 * The caller passes the object URL it already holds for the thumbnail. That is safe because the lightbox is rendered by
 * the thumbnail itself: when the thumbnail unmounts (switching conversations) the lightbox goes with it, so there is no
 * second window left holding a URL that has been revoked.
 */
export const ImageLightbox: React.FC<{ url: string; fileName: string; onClose: () => void }> = ({ url, fileName, onClose }) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previous?.focus?.();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={fileName}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483000,
      }}
    >
      <button
        ref={closeRef}
        type="button"
        aria-label="Close"
        data-testid="image-lightbox-close"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: 16,
          insetInlineEnd: 16,
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(255,255,255,0.92)',
          color: '#111',
          fontSize: 20,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        {'×'}
      </button>
      <img
        src={url}
        alt={fileName}
        data-testid="image-lightbox-img"
        onClick={(event) => event.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, background: '#fff', cursor: 'default' }}
      />
    </div>,
    document.body,
  );
};
